import {ChangeDetectionStrategy, Component, computed, ElementRef, Inject, InjectionToken, input, OnDestroy, OnInit, output, signal} from "@angular/core";
import {EnkelGruppeModell, FargeHex, MøteMedAggregaterModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AsyncPipe, NgTemplateOutlet} from "@angular/common";
import {ElementSynlighetObservering} from "felles-ng2/tjenester/ElementSynlighetObservering";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {fadeInnUtAnimasjon} from "web-ng2/delt/animasjoner";
import {TranslateModule} from "@ngx-translate/core";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {gruppefargeNilsikker} from "felles-ng2/modell/Gruppefarge";
import {MatTooltip} from "@angular/material/tooltip";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TemporalFormatering} from "felles-ng2/tjenester/TemporalFormatering";
import {lesMøtedato2} from "felles-ng2/modell/møteModellHjelpere";
import {MatIcon} from "@angular/material/icon";
import {IkonMedTellerKomponent} from "web-ng2/delt/komponenter/ikon-med-teller/ikon-med-teller";
import {plainDateFraZonedDateTime} from "felles-ng2/støttefunksjoner/temporal";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {concat, interval, Observable, of, switchMap} from "rxjs";
import {map} from "rxjs/operators";
import {toObservable} from "@angular/core/rxjs-interop";
import {Temporal} from "temporal-polyfill";
import {filterNil} from "@ngneat/elf";

const debug = initDebugger('HorisontalMøtenavigasjonInnslagKomponent');

/**  InjectionToken for injisering av {@link ElementSynlighetObservering} instans i {@link HorisontalMøtenavigasjonInnslagKomponent}. */
export const HorisontalMøtenavigasjonInnslagKomponent_ElementSynlighetObservering_DI_TOKEN = new InjectionToken<ElementSynlighetObservering>('ElementSynlighetObservering for HorisontalMøtenavigasjonInnslagKomponent');

/**
 * Innslag i horisontal-møtenavigasjon
 */
@Component({
    selector: 'horisontal-moetenavigasjon-innslag',
    templateUrl: 'horisontal-møtenavigasjon-innslag.html',
    styleUrl: 'horisontal-møtenavigasjon-innslag.scss',
    host: {
        'data-testid': 'horisontal-moetenavigasjon-innslag',
        '(click)': 'vedKlikk.emit()',
        '[id]': 'innslag().id',
        '[class]': 'innslagNavn()',
        '[class.aktiv]': 'aktiv()',
        '[class.smal-visning]': 'smalVisning()',
        '[class.smal-visning-ekspandert]': 'smalVisningEkspandert()',
        '[style.border-top-color]': 'innslag().gruppefarge',
        '[style.--bredde]': 'bredde()+"px"',
        '[attr.data-type]': 'innslag().type()'
    },
    animations: [fadeInnUtAnimasjon('0.2s')],
    imports: [
        NgTemplateOutlet,
        TranslateModule,
        ZoneddatetimeSomPlainTimePipe,
        FormaterPlainTimePipe,
        MatTooltip,
        MatIcon,
        IkonMedTellerKomponent,
        AsyncPipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorisontalMøtenavigasjonInnslagKomponent implements OnInit, OnDestroy {

    /** Fyrer nå elementet blir synlig i skrollkontaineren */
    vedSynlig = output();
    vedKlikk = output();

    innslag = input.required<Innslag>();
    aktiv = input(false);
    bredde = input(200);
    smalVisning = input(false);
    smalVisningEkspandert = input(false);

    innslagNavn = computed(() => InnslagType[this.innslag().type()]);
    moete = computed(() => this.innslag().moeteinfo());
    moetedato: Observable<string>;

    public readonly innslagTyper = InnslagType

    constructor(public element: ElementRef<HTMLElement>,
                @Inject(HorisontalMøtenavigasjonInnslagKomponent_ElementSynlighetObservering_DI_TOKEN) private elementSynlighet: ElementSynlighetObservering,
                private oversettelser: Oversettelser,
                private tidssone: GjeldendeTidssone,
                private klokke: Klokke,
                private temporalFormatering: TemporalFormatering) {

        this.moetedato = toObservable(this.moete).pipe(
          filterNil(),
          map(møte => plainDateFraZonedDateTime(lesMøtedato2(møte.moete).start, this.tidssone.brukersTidssone())),
          switchMap(dato => concat(of(dato), interval(1000)).pipe(map(() => dato))),
          map(dato => this.formatertMøtedato(dato))
        );
    }

    moeteTooltip() {
        return this.oversettelser.oversett('{dato} - {moetenavn} {moetenummer} - {gruppetittel} i {organisasjonsnavn}', {
            dato: this.temporalFormatering.formaterZonedDateTime(lesMøtedato2(this.innslag().moeteinfo()!.moete).start, 'long'),
            moetenummer: this.innslag().moeteinfo()!.moete.nummer,
            moetenavn: this.innslag().moeteinfo()!.moete.tittel,
            gruppetittel: this.innslag().gruppe?.tittel,
            organisasjonsnavn: this.innslag().gruppe?.organisasjonsnavn
        });
    }

    ngOnInit(): void {
        this.elementSynlighet.nårElementBlirSynlig(this.element.nativeElement, () => this.nårInnslagHarBlittSynlig())
    }

    ngOnDestroy(): void {
        this.elementSynlighet.avregistrer(this.element.nativeElement)
    }

    // private hjelpemetoder

    private formatertMøtedato(dato: Temporal.PlainDate): string {
        if (this.innslag().type() != InnslagType.moete) return '';
        return this.klokke.nåSomZonedDateTime(this.tidssone.brukersTidssone()).year == dato.year ?
          this.temporalFormatering.formaterPlainDateRelativ(dato, {weekday: 'short', day: 'numeric', month: 'short'}, 1) :
          this.temporalFormatering.formaterPlainDate(dato, 'short');
    }

    private nårInnslagHarBlittSynlig() {
        //debug(`synlig ${this.toString()}`);
        this.vedSynlig.emit();
    }
}

export enum InnslagType {ingenTidligere, stedfortrederFoer, moete, stedfortrederEtter, ingenSenere}

export type Møteinfo = { møte: MøteMedAggregaterModell, gruppe: EnkelGruppeModell | null }

/** Innslag som representerer et synlig element i den horisontale navigasjonen.
 * Kan være enten et møte eller en stedfortreder for tidligere/senere møter eller markør for at det ikke finnes flere tidligere eller senere møter.
 * MERK at innslag er meterbare - både typen og møteinformasjon kan endres. Dette slik at
 * overgangen fra stedfortreder til møte kan animeres (se bruk av fadeInnUt animasjon) */
export class Innslag {
    private static nesteId = 0;

    static stedfortredere(antall: number, type: InnslagType) {
        return new Array(antall).fill(null).map(() => Innslag.stedfortreder(type));
    }

    static stedfortreder(type: InnslagType) {
        return new Innslag(null, type);
    }

    static møte(møteinfo: Møteinfo) {
        return new Innslag(møteinfo, InnslagType.moete);
    }

    readonly id: string;
    readonly type = signal<InnslagType>(InnslagType.moete);
    readonly moeteinfo = signal<MøteMedAggregaterModell | null>(null);
    //disse trenger ikke å være signal da de ikke muterer etter at type har blitt endret til moete
    gruppe: EnkelGruppeModell | null = null;
    gruppefarge: FargeHex = 'transparent';

    constructor(moeteinfo: Møteinfo | null, type: InnslagType) {
        this.id = 'innslag_' + Innslag.nesteId++;
        this.type.set(type);
        if (erIkkeNil(moeteinfo)) this.initMøteinfo(moeteinfo);

    }

    initMøteinfo(moeteinfo: Møteinfo) {
        this.moeteinfo.set(moeteinfo.møte);
        this.gruppe = moeteinfo.gruppe
        this.gruppefarge = gruppefargeNilsikker(moeteinfo.gruppe).rammeFarge;
        this.type.set(InnslagType.moete);
    }

    oppdaterMøte(møte: MøteMedAggregaterModell): Innslag {
        this.moeteinfo.set(møte);
        return this;
    }

    toString(): string {
        return `${InnslagType[this.type()]}[${this.id}](${this.type() == InnslagType.moete ? this.moeteinfo()!.moete.id + '/' + this.moeteinfo()!.moete.dato.start : ''})`;
    }
}
